import React from 'react';
import { Lock, Sparkles } from 'lucide-react';
import FormSelect from '../../UI/FormSelect';
import { labelForValue } from './workPassApplicationSchema';

/**
 * WorkPassField — renders a single Work Pass Application field.
 *
 * Backend-agnostic and presentational: it knows nothing about the API. It
 * receives the field definition + current value and reports changes upward.
 *
 * Behaviour by `editable`:
 *   - editable  → renders the appropriate control (input / select / textarea)
 *   - !editable → renders a read-only value box (review / locked-prefill modes)
 *
 * Accessibility:
 *   - every control has an id and an associated <label htmlFor>
 *   - required fields set aria-required and render a visual + sr-only marker
 *   - hint/error are linked via aria-describedby; errors use role="alert"
 *   - prefilled state is announced via sr-only text, not colour alone
 *
 * @param {object}   field        schema field definition
 * @param {*}        value        current value
 * @param {function} onChange     (key, value) => void   (only used when editable)
 * @param {string}   [error]      validation message to display
 * @param {boolean}  [editable]   whether the control is interactive (default true)
 * @param {boolean}  [prefilled]  value came prefilled from the ATS (Form A)
 * @param {boolean}  [locked]     prefilled AND not editable (shows a lock affordance)
 * @param {boolean}  [disabled]   disable the control (e.g. while submitting)
 * @param {'admin'|'employee'} [theme]  colour theme (default 'employee')
 */
const WorkPassField = ({
  field,
  value,
  onChange,
  error,
  editable = true,
  prefilled = false,
  locked = false,
  disabled = false,
  theme = 'employee',
}) => {
  const isAdmin = theme === 'admin';
  const id = `wpf-${field.key}`;
  const hintId = field.hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  const inputClass = `${isAdmin ? 'input-admin' : 'input'} ${
    error ? 'border-red-400' : ''
  }`.trim();
  const textareaClass = `${isAdmin ? 'textarea-admin' : 'textarea'} ${
    error ? 'border-red-400' : ''
  }`.trim();

  const handleInput = (e) => onChange?.(field.key, e.target.value);

  const renderControl = () => {
    // Read-only / locked presentation
    if (!editable) {
      const display = labelForValue(field, value);
      return (
        <div
          className={`flex min-h-[44px] items-center justify-between gap-2 rounded-xl border px-4 py-2.5 text-sm ${
            locked ? 'border-gray-200 bg-gray-50 text-gray-700' : 'border-gray-200 bg-white text-gray-900'
          }`}
        >
          <span className={display ? '' : 'italic text-gray-400'}>
            {display || 'Not provided yet'}
          </span>
          {locked && (
            <Lock className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" aria-hidden="true" />
          )}
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <FormSelect
          theme={theme}
          options={[{ value: '', label: `Select ${field.label.toLowerCase()}…` }, ...(field.options || [])]}
          value={value ?? ''}
          onChange={(val) => onChange?.(field.key, val)}
          disabled={disabled}
          required={field.required}
        />
      );
    }

    if (field.type === 'textarea') {
      return (
        <textarea
          id={id}
          className={textareaClass}
          value={value ?? ''}
          onChange={handleInput}
          placeholder={field.placeholder}
          disabled={disabled}
          aria-required={field.required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          rows={3}
        />
      );
    }

    return (
      <input
        id={id}
        type={field.type === 'tel' ? 'tel' : field.type === 'email' ? 'email' : field.type}
        className={inputClass}
        value={value ?? ''}
        onChange={handleInput}
        placeholder={field.placeholder}
        autoComplete={field.autoComplete}
        disabled={disabled}
        aria-required={field.required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
    );
  };

  // FormSelect renders its own <button>, so we associate the label by id only
  // for native controls; for selects the label still provides a visible caption.
  const labelProps = field.type === 'select' || !editable ? {} : { htmlFor: id };

  return (
    <div className={field.colSpan === 2 ? 'sm:col-span-2' : ''}>
      <div className="mb-1 flex items-center gap-2">
        <label
          {...labelProps}
          className="block text-xs font-medium uppercase tracking-wide text-gray-500"
        >
          {field.label}
          {field.required && (
            <>
              <span aria-hidden="true" className="ml-0.5 text-red-500">
                *
              </span>
              <span className="sr-only"> (required)</span>
            </>
          )}
        </label>

        {prefilled && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
              isAdmin ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-700'
            }`}
            title="Prefilled from your application (ATS)"
          >
            <Sparkles className="h-2.5 w-2.5" aria-hidden="true" />
            From application
            <span className="sr-only">— prefilled, please review</span>
          </span>
        )}

        {field.sensitive && (
          <span
            className="text-[10px] font-medium text-gray-400"
            title="Sensitive personal data — stored encrypted and removed if you do not join"
          >
            sensitive
          </span>
        )}
      </div>

      {renderControl()}

      {field.hint && !error && (
        <p id={hintId} className="mt-1 text-xs text-gray-400">
          {field.hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export default React.memo(WorkPassField);
